<template>
  <div v-if="show" class="email-modal-overlay" @click="handleOverlayClick">
    <div class="email-modal" @click.stop>
      <div class="email-modal-content">
        <h2 class="email-modal-title">{{ title }}</h2>
        <p class="email-modal-description">
          {{ description }}
        </p>
        
        <form @submit.prevent="handleSubmit" class="email-modal-form">
          <div class="email-modal-field">
            <label for="fullName" class="email-modal-label">Full Name</label>
            <input
              id="fullName"
              v-model="fullName"
              type="text"
              required
              class="email-modal-input"
              placeholder="John Doe"
              :disabled="submitting"
            />
          </div>

          <div class="email-modal-field">
            <label for="email" class="email-modal-label">Email Address</label>
            <input
              id="email"
              v-model="email"
              type="email"
              required
              class="email-modal-input"
              placeholder="your.email@example.com"
              :disabled="submitting"
            />
          </div>

          <div class="email-modal-field">
            <label for="phone" class="email-modal-label">Phone Number</label>
            <input
              id="phone"
              v-model="phone"
              type="tel"
              required
              class="email-modal-input"
              :class="{ 'input-warning': phoneWarning }"
              placeholder="+1 (555) 123-4567"
              :disabled="submitting"
            />
            <div v-if="phoneWarning" class="email-modal-warning">
              {{ phoneWarning }}
            </div>
          </div>
          
          <div v-if="error" class="email-modal-error">
            {{ error }}
          </div>
          
          <div class="email-modal-buttons">
            <button
              type="button"
              @click="handleCancel"
              class="email-modal-cancel"
              :disabled="submitting"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="email-modal-submit"
              :disabled="submitting || !email || !fullName || !phone"
            >
              {{ submitting ? 'Submitting...' : 'Continue' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'EmailModal',

  props: {
    show: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'Enter Your Email'
    },
    description: {
      type: String,
      default: 'Please provide your email address to continue.'
    },
    calculation: {
      type: String,
      default: null
    },
    closeOnOverlayClick: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      fullName: '',
      email: '',
      phone: '',
      submitting: false,
      error: null,
      phoneWarning: null
    };
  },

  watch: {
    show(newVal) {
      if (newVal) {
        // Check if user info is already stored
        const storedEmail = sessionStorage.getItem('public_view_email');
        const storedName = sessionStorage.getItem('public_view_name');
        const storedPhone = sessionStorage.getItem('public_view_phone');
        
        if (storedEmail) {
          this.email = storedEmail;
        }
        if (storedName) {
          this.fullName = storedName;
        }
        if (storedPhone) {
          this.phone = storedPhone;
        }
      } else {
        // Reset form when modal is closed
        this.fullName = '';
        this.email = '';
        this.phone = '';
        this.error = null;
        this.phoneWarning = null;
      }
    },
    
    phone(newVal) {
      // Check for dummy phone numbers and warn user
      if (newVal) {
        const cleanPhone = newVal.replace(/[^0-9]/g, '');
        const dummyPatterns = [
          '1234567890', '0000000000', '1111111111', '2222222222',
          '3333333333', '4444444444', '5555555555', '6666666666',
          '7777777777', '8888888888', '9999999999'
        ];
        
        if (dummyPatterns.includes(cleanPhone)) {
          this.phoneWarning = 'Please enter a valid phone number (not a test number).';
        } else {
          this.phoneWarning = null;
        }
      } else {
        this.phoneWarning = null;
      }
    }
  },

  methods: {
    async handleSubmit() {
      this.error = null;
      this.submitting = true;

      try {
        // Submit user info to backend
        const response = await axios.post('/api/public/submit-email', {
          full_name: this.fullName,
          email: this.email,
          phone: this.phone,
          calculation: this.calculation
        });

        if (response.data.success) {
          // Store user info in session storage
          sessionStorage.setItem('public_view_email', this.email);
          sessionStorage.setItem('public_view_name', this.fullName);
          sessionStorage.setItem('public_view_phone', this.phone);
          
          // Emit success event with user info
          this.$emit('submit', {
            email: this.email,
            fullName: this.fullName,
            phone: this.phone
          });
          
          // Close modal
          this.$emit('update:show', false);
        } else {
          this.error = response.data.message || 'Failed to submit information. Please try again.';
        }
      } catch (error) {
        console.error('Error submitting user info:', error);
        
        if (error.response && error.response.data && error.response.data.message) {
          this.error = error.response.data.message;
        } else if (error.response && error.response.data && error.response.data.errors) {
          // Handle validation errors
          const errors = error.response.data.errors;
          this.error = Object.values(errors).flat().join(', ');
        } else {
          this.error = 'An error occurred. Please try again.';
        }
      } finally {
        this.submitting = false;
      }
    },

    handleCancel() {
      this.$emit('cancel');
      this.$emit('update:show', false);
    },

    handleOverlayClick() {
      if (this.closeOnOverlayClick) {
        this.handleCancel();
      }
    }
  }
};
</script>

<style scoped>
/* Email Modal Styles */
.email-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.email-modal {
  background: var(--card);
  border-radius: 0.75rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 28rem;
  width: 90%;
  margin: 1rem;
}

.email-modal-content {
  padding: 2rem;
}

.email-modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--foreground);
  margin-bottom: 0.5rem;
}

.email-modal-description {
  font-size: 0.875rem;
  color: var(--muted-foreground);
  margin-bottom: 1.5rem;
}

.email-modal-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.email-modal-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.email-modal-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--foreground);
}

.email-modal-input {
  width: 100%;
  padding: 0.625rem 0.875rem;
  font-size: 0.875rem;
  background-color: var(--input);
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  color: var(--foreground);
  transition: all 0.2s ease;
}

.email-modal-input:focus {
  border-color: var(--primary);
  outline: none;
  box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.1);
}

.email-modal-input:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.email-modal-input.input-warning {
  border-color: #f59e0b;
}

.email-modal-warning {
  padding: 0.5rem 0.75rem;
  background-color: #fef3c7;
  color: #92400e;
  border-radius: 0.375rem;
  font-size: 0.8125rem;
  margin-top: 0.25rem;
}

.email-modal-error {
  padding: 0.75rem;
  background-color: var(--destructive);
  color: var(--destructive-foreground);
  border-radius: 0.5rem;
  font-size: 0.875rem;
}

.email-modal-buttons {
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.email-modal-cancel,
.email-modal-submit {
  flex: 1;
  padding: 0.625rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 0.5rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.email-modal-cancel {
  background-color: var(--muted);
  color: var(--foreground);
}

.email-modal-cancel:hover:not(:disabled) {
  background-color: var(--muted);
  opacity: 0.8;
}

.email-modal-submit {
  background-color: var(--primary);
  color: var(--primary-foreground);
}

.email-modal-submit:hover:not(:disabled) {
  opacity: 0.9;
}

.email-modal-cancel:disabled,
.email-modal-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
